import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as API from "../../../utils/apiPath";
import { agent } from "../../../utils/agent";

// Async Thunks (replacing loadData, saveSelectMentor, saveWaitlistMentor)
export const loadMentors = createAsyncThunk(
  "mentors/loadMentors",
  async ({ request, userId, roleId = "Mentor", domains = "" }, { rejectWithValue }) => {
    try {
      const res = await agent.get(
        `${API.LOAD_MENTORS}/${userId}/${roleId}?userDomains=${domains}`,
        { params: request }
      );

      const data = res?.data?.data || {};

      return {
        mentors: data.data || [],
        pagination: data.pagination || {},
        notFound: (data.data || []).length === 0,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadRegMentors = createAsyncThunk(
  "mentors/loadRegMentors",
  async ({ request, userId, roleId = "Mentor", domains = "" }, { rejectWithValue }) => {
    try {
      const res = await agent.get(
        `${API.LOAD_MENTORS_REG}/${userId}/${roleId}?userDomains=${domains}`,
        { params: request }
      );

      const data = res?.data?.data || {};

      return {
        mentors: data.data || [],
        pagination: data.pagination || {},
        steps_labels: data.steps_labels || {},
        notFound: (data.data || []).length === 0,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ----------------------------------------
// SAVE SELECT MENTOR (REPLACES saveSelectMentor)
// ----------------------------------------
export const selectMentor = createAsyncThunk(
  "mentors/selectMentor",
  async ({ userId, mentorId }, { rejectWithValue }) => {
    try {
      const res = await agent.post(API.SAVESELECTMENTOR, { userId, mentorId });
      return res.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ----------------------------------------
// WAITLIST FOR AVAILABILITY (saveWaitlistMentor)
// ----------------------------------------
export const waitlistMentor = createAsyncThunk(
  "mentors/waitlistMentor",
  async ({ userId, mentorId }, { rejectWithValue }) => {
    try {
      const res = await agent.post(API.SAVEWAITLISTMENTOR, { userId, mentorId });
      return res.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadMentorDetail = createAsyncThunk(
  "mentors/loadMentorDetail",
  async ({ userId, mentorId, roleId = "Mentor", domains = "" }, { rejectWithValue }) => {
    try {
      const res = await agent.get(
        `${API.GET_MENTOR_DETAIL_DATA}/${mentorId}/${roleId}?type=suggested_mentor`
      );
      // The detail endpoint returns a single object or array; handle both
      const data = res?.data?.data;
      return Array.isArray(data) ? data[0] : data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAllProgrammes = createAsyncThunk(
  "app/getAllProgrammes",
  async (_, thunkAPI) => {
    try {
      const response = await agent.get(API.LOAD_PROGRAMMES + "/all");
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const mentorsSlice = createSlice({
  name: "mentors",
  initialState: {
    mentorsData: [],
    steps_labels: {},
    pagination: {},
    loading: false,
    isProcessing: false,
    notFound: false,
    error: null,
    lastMessage: "",
    formPopup: null,
  },

  reducers: {
    loadFormPop: (state, action) => {
      state.formPopup = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // LOAD
      .addCase(loadMentors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMentors.fulfilled, (state, action) => {
        state.loading = false;
        state.mentorsData = action.payload.mentors;
        state.pagination = action.payload.pagination;
        state.notFound = action.payload.notFound;
      })
      .addCase(loadMentors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.notFound = true;
      })
      .addCase(loadRegMentors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadRegMentors.fulfilled, (state, action) => {
        state.loading = false;
        state.mentorsData = action.payload.mentors;
        state.pagination = action.payload.pagination;
        state.steps_labels = action.payload.steps_labels;
        state.notFound = action.payload.notFound;
      })
      .addCase(loadRegMentors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.notFound = true;
      })

      // SELECT
      .addCase(selectMentor.pending, (state) => {
        state.isProcessing = true;   // ✅ start loader
      })
      .addCase(selectMentor.fulfilled, (state, action) => {
        state.isProcessing = false;  // ✅ stop loader
        state.lastMessage = action.payload?.message;
      })
      .addCase(selectMentor.rejected, (state) => {
        state.isProcessing = false;  // ✅ stop loader
      })


      // WAITLIST
      .addCase(waitlistMentor.pending, (state) => {
        state.isProcessing = true;   // ✅ start loader
      })
      .addCase(waitlistMentor.fulfilled, (state, action) => {
        state.isProcessing = false;  // ✅ stop loader
        state.lastMessage = action.payload?.message;
      })
      .addCase(waitlistMentor.rejected, (state) => {
        state.isProcessing = false;  // ✅ stop loader
      })

      // MENTOR DETAIL
      .addCase(loadMentorDetail.pending, (state) => {
        state.isProcessing = true;
      })
      .addCase(loadMentorDetail.fulfilled, (state) => {
        state.isProcessing = false;
      })
      .addCase(loadMentorDetail.rejected, (state) => {
        state.isProcessing = false;
      });
  },
});

export const { loadFormPop } = mentorsSlice.actions;
export default mentorsSlice.reducer;